import React, { useCallback } from 'react'
import classnames from 'classnames/bind'
import { connect } from 'react-redux'
import logger from '@adc/logger'
import {
  Text,
  Loader,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
} from '@adc/ui-morpheus'
import type { PartyBuyingPreferenceInput } from '@adc/sdk/types'
import UserProfileCard from '../../../components/userProfileCard'
import UserBuyingCriteria from '../../../components/userBuyingCriteria'
import { useUserProfile } from '../../../hooks/useUserProfile'
import { useUserByPhone } from '../../../hooks/useUserByPhone'
import { useBuyingPreference } from '../../../hooks/useBuyingPreference'
import { extractPhoneFromUserUrn } from './utils'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface StoreProps {
  currentUserAuthorities: ReadonlyArray<string>
  featureFlags: Record<string, boolean>
}

export interface UserInsightsPageProps extends StoreProps {
  match: {
    params: {
      // Overloaded: a numeric user ID, or an adc:user:phone-{E.164} URN.
      userId?: string
    }
  }
}

const UserInsightsPage: React.FC<UserInsightsPageProps> = ({
  match,
  currentUserAuthorities,
  featureFlags,
}) => {
  const { userId: refParam } = match.params
  const hasAccess = currentUserAuthorities.includes('UM')
  const ref = refParam ? decodeURIComponent(refParam) : null
  const phoneNumber = ref ? extractPhoneFromUserUrn(ref) : null
  const isPhoneMode = phoneNumber !== null
  const userIdFromRef = isPhoneMode ? null : ref
  const {
    userId: resolvedUserId,
    isLoading: isResolvingPhone,
    error: phoneError,
  } = useUserByPhone(hasAccess && isPhoneMode ? phoneNumber : null)

  const effectiveUserId = isPhoneMode ? resolvedUserId : userIdFromRef

  const { userProfile, isLoading, error } = useUserProfile(
    hasAccess ? effectiveUserId : null
  )
  const {
    preference: buyingPreference,
    isSaving,
    savePreference,
  } = useBuyingPreference(userProfile?.party_id ?? null)

  const notifications = useApplicationNotifications()

  const handleSavePreference = useCallback(
    (input: PartyBuyingPreferenceInput) => {
      savePreference(input)
        .then(() => {
          notifications.toast.open({ title: 'Buying criteria saved' })
        })
        .catch(err => {
          logger.error('[UserInsights] Failed to save preference:', err)
          notifications.toast.open({
            title: 'Failed to save buying criteria',
            role: NOTIFICATION_ROLES.ERROR,
          })
        })
    },
    [savePreference, notifications]
  )

  if (!hasAccess) {
    return (
      <div className={cx('page')}>
        <Text size='small' color='error'>
          You do not have permission to view this page.
        </Text>
      </div>
    )
  }

  if ((isResolvingPhone || isLoading) && !userProfile) {
    return (
      <div className={cx('page')}>
        <Loader active />
      </div>
    )
  }

  // Phone resolved but no verified user matches the number.
  if (
    isPhoneMode &&
    !isResolvingPhone &&
    !phoneError &&
    resolvedUserId === null
  ) {
    return (
      <div className={cx('page')}>
        <Text size='small' color='error'>
          No verified user found for this phone number.
        </Text>
      </div>
    )
  }

  if ((phoneError || error) && !userProfile) {
    return (
      <div className={cx('page')}>
        <Text size='small' color='error'>
          Unable to load user profile.
        </Text>
      </div>
    )
  }

  if (!userProfile) return null

  return (
    <div className={cx('page')}>
      <UserProfileCard user={userProfile} />
      <UserBuyingCriteria
        preference={buyingPreference}
        onSave={handleSavePreference}
        isSaving={isSaving}
        featureFlags={featureFlags}
      />
    </div>
  )
}

const mapStateToProps = (store: {
  user: { authorities?: string[] }
  featureFlags: Record<string, boolean>
}): StoreProps => ({
  currentUserAuthorities: store.user.authorities ?? [],
  featureFlags: store.featureFlags,
})

export default connect(mapStateToProps)(UserInsightsPage)
